import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import useProduction from '@/src/Production/components/hooks/useProduction';
import SearchField from '../../components/SearchField';
import PeriodFilter, { PeriodKey } from '../../components/PeriodFilter';
import FilterMenu, {
    FilterDimension,
    ActiveFilter,
} from '../../components/FilterMenu';
import {
    IconPipeline,
    IconStaff,
    IconContacts,
} from '../../components/icons';
import { STATUS_LABELS, formatEventType } from './shared';

/*
 * Jobs — the pipeline at engagement altitude (mock: job-detail.html's
 * parent). One row per job (multi-day runs and tours roll up); jobless
 * productions appear as single rows so nothing hides. Pending Attio
 * deals live here until won.
 */

interface JobRow {
    jobId: number | null;
    activationId?: number;
    name: string;
    activationCount: number;
    startAt?: string;
    endAt?: string;
    billingEntity?: string;
    boothType?: string;
    eventType?: string;
    statusCounts: Record<string, number>;
    crewCount: number;
    isJob: boolean;
}

const periodEnd = (period: PeriodKey): moment.Moment | null => {
    switch (period) {
        case 'today': return moment().endOf('day');
        case 'tomorrow': return moment().add(1, 'day').endOf('day');
        case 'this_week': return moment().endOf('week');
        case 'this_month': return moment().endOf('month');
        case 'this_year': return moment().endOf('year');
        case 'next_7': return moment().add(7, 'days').endOf('day');
        case 'next_30': return moment().add(30, 'days').endOf('day');
        case 'next_6m': return moment().add(6, 'months').endOf('day');
        case 'next_12m': return moment().add(12, 'months').endOf('day');
        default: return null;
    }
};

const isPipelineOnly = (row: JobRow) =>
    Object.keys(row.statusCounts).every((s) => s === 'pending' || s === 'lost');
const isLostOnly = (row: JobRow) =>
    Object.keys(row.statusCounts).every((s) => s === 'lost');

/* the dominant lifecycle stage for the rollup chip */
const STATUS_ORDER = ['show_ready', 'in_production', 'ready_for_production', 'pending', 'past', 'wrapped', 'lost'];
const dominantStatus = (row: JobRow) =>
    STATUS_ORDER.find((s) => row.statusCounts[s]) ?? 'pending';

const STATUS_CHIP: Record<string, string> = {
    pending: 'bg-sunk text-muted-foreground border-[1.5px] border-dashed border-off',
    ready_for_production: 'bg-brand-soft text-brand-ink',
    in_production: 'bg-good-soft text-good',
    show_ready: 'bg-good text-white',
    past: 'bg-sunk text-faint',
    wrapped: 'bg-sunk text-faint',
    lost: 'border border-attention/40 text-attention',
};

export default function JobsV2() {
    const navigate = useNavigate();
    const { getJobs } = useProduction();
    const [rows, setRows] = useState<JobRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState<PeriodKey>('next_6m');
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState<ActiveFilter[]>([]);

    useEffect(() => {
        let mounted = true;
        (async () => {
            const data = await getJobs().catch(() => []);
            if (!mounted) return;
            setRows(
                (data ?? []).map((r: any) => ({
                    ...r,
                    jobId: r.jobId ? +r.jobId : null,
                    activationId: r.activationId ? +r.activationId : undefined,
                    activationCount: +(r.activationCount ?? 1),
                    crewCount: +(r.crewCount ?? 0),
                    statusCounts: r.statusCounts ?? {},
                })),
            );
            setLoading(false);
        })();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const dimensions: FilterDimension[] = useMemo(() => {
        const distinct = (values: (string | undefined)[]) =>
            Array.from(new Set(values.filter(Boolean) as string[])).sort(
                (a, b) => a.localeCompare(b),
            );
        return [
            {
                key: 'status',
                label: 'Status',
                icon: <IconPipeline />,
                values: distinct(
                    rows.flatMap((r) =>
                        Object.keys(r.statusCounts).map(
                            (s) => STATUS_LABELS[s],
                        ),
                    ),
                ),
            },
            {
                key: 'eventType',
                label: 'Event type',
                icon: <IconContacts />,
                values: distinct(rows.map((r) => formatEventType(r.eventType))),
            },
            {
                key: 'billingEntity',
                label: 'Billing entity',
                icon: <IconStaff />,
                values: distinct(rows.map((r) => r.billingEntity)),
            },
        ];
    }, [rows]);

    const addFilter = (filter: ActiveFilter) => {
        setFilters((prev) => [
            ...prev.filter((f) => f.key !== filter.key),
            filter,
        ]);
    };

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const end = periodEnd(period);
        return rows.filter((r) => {
            if (q && !r.name.toLowerCase().includes(q)) return false;
            if (!q && end && r.startAt && moment(r.startAt).isAfter(end))
                return false;
            for (const f of filters) {
                if (
                    f.key === 'status' &&
                    !Object.keys(r.statusCounts).some(
                        (s) => STATUS_LABELS[s] === f.value,
                    )
                )
                    return false;
                if (
                    f.key === 'eventType' &&
                    formatEventType(r.eventType) !== f.value
                )
                    return false;
                if (f.key === 'billingEntity' && r.billingEntity !== f.value)
                    return false;
            }
            return true;
        });
    }, [rows, search, period, filters]);

    const groups = useMemo(() => {
        const pipeline = filtered.filter(isPipelineOnly);
        const active = filtered.filter((r) => !isPipelineOnly(r));
        const dayStart = moment().startOf('day');
        const weekEnd = moment().endOf('week');
        const earlier = active.filter(
            (r) =>
                r.endAt && moment(r.endAt).isBefore(dayStart),
        );
        const thisWeek = active.filter(
            (r) =>
                r.startAt &&
                !earlier.includes(r) &&
                moment(r.startAt).isSameOrBefore(weekEnd),
        );
        const later = active.filter(
            (r) =>
                !earlier.includes(r) &&
                !thisWeek.includes(r),
        );
        return [
            { label: 'This week', rows: thisWeek },
            { label: 'Later', rows: later },
            { label: 'Pipeline · from Attio', rows: pipeline },
            { label: 'Earlier', rows: [...earlier].reverse() },
        ].filter((g) => g.rows.length > 0);
    }, [filtered]);

    const dateRange = (r: JobRow) => {
        if (!r.startAt) return null;
        const start = moment(r.startAt);
        const end = r.endAt ? moment(r.endAt) : start;
        return end.isSame(start, 'day')
            ? start.format('MMM D')
            : `${start.format('MMM D')} – ${end.format(
                  end.isSame(start, 'month') ? 'D' : 'MMM D',
              )}`;
    };

    const row = (r: JobRow) => {
        const status = dominantStatus(r);
        return (
            <div
                key={r.jobId ?? `a${r.activationId}`}
                onClick={() =>
                    r.isJob && r.jobId
                        ? navigate(`/v2/jobs/${r.jobId}`)
                        : navigate(`/v2/activations/${r.activationId}`)
                }
                className={
                    'flex cursor-pointer items-center gap-3.5 border-t border-line px-0.5 py-[11px] text-[12.5px] first:border-t-0 hover:bg-sunk ' +
                    (isLostOnly(r)
                        ? 'opacity-60'
                        : isPipelineOnly(r)
                          ? 'opacity-80'
                          : '')
                }
            >
                <span
                    className={
                        'w-[96px] flex-none rounded-[9px] py-1.5 text-center text-[12px] font-semibold tabular-nums ' +
                        (isPipelineOnly(r)
                            ? 'border-[1.5px] border-dashed border-border text-faint'
                            : 'bg-secondary')
                    }
                >
                    {dateRange(r) ?? 'TBD'}
                </span>
                <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold tracking-[-0.01em]">
                        {r.name}
                    </span>
                    <span className="mt-px block truncate text-[12px] text-muted-foreground">
                        {r.activationCount === 1
                            ? formatEventType(r.eventType)
                            : `${r.activationCount} activations`}
                        {r.crewCount ? ` · ${r.crewCount} crew` : ''}
                    </span>
                </span>
                <span
                    className={
                        'flex-none rounded-full px-2.5 py-[3px] text-[10.5px] font-semibold ' +
                        (STATUS_CHIP[status] ?? 'bg-sunk text-muted-foreground')
                    }
                >
                    {STATUS_LABELS[status]}
                    {r.activationCount > 1 &&
                    (r.statusCounts[status] ?? 0) < r.activationCount
                        ? ` ${r.statusCounts[status]}/${r.activationCount}`
                        : ''}
                </span>
                <span className="ps-label w-[92px] flex-none normal-case tracking-[.08em]">
                    {r.boothType ? r.boothType.replaceAll('_', ' ') : '—'}
                </span>
                <span className="w-[110px] flex-none truncate text-[11.5px] text-faint">
                    {r.billingEntity ?? '—'}
                </span>
                <span className="flex-none text-faint">→</span>
            </div>
        );
    };

    return (
        <main className="mx-auto max-w-[1060px] px-[clamp(18px,3vw,34px)] pt-4">
            <div className="ps-label mb-3.5">Jobs</div>

            <div className="mb-3 flex flex-wrap items-center gap-3">
                <PeriodFilter value={period} onChange={setPeriod} />
                <div className="flex-1" />
                <SearchField
                    value={search}
                    onChange={setSearch}
                    placeholder="Search jobs…"
                    className="w-full max-w-[260px]"
                />
                <FilterMenu dimensions={dimensions} onAdd={addFilter} />
            </div>

            {filters.length > 0 && (
                <div className="mb-4 flex flex-wrap gap-2">
                    {filters.map((f) => (
                        <span
                            key={f.key}
                            className="inline-flex items-center gap-1.5 rounded-full border-[1.5px] border-dashed border-border px-[11px] py-[5px] text-xs text-muted-foreground"
                        >
                            <span className="flex text-faint">
                                {dimensions.find((d) => d.key === f.key)?.icon}
                            </span>
                            {f.label} is <b>{f.value}</b>
                            <button
                                type="button"
                                onClick={() =>
                                    setFilters((prev) =>
                                        prev.filter((x) => x.key !== f.key),
                                    )
                                }
                                className="cursor-pointer border-0 bg-transparent p-0 text-faint hover:text-foreground"
                            >
                                ✕
                            </button>
                        </span>
                    ))}
                </div>
            )}

            {loading && (
                <div className="py-10 text-[13px] text-faint">Loading…</div>
            )}
            {!loading && groups.length === 0 && (
                <div className="py-10 text-[13px] text-faint">
                    No jobs match.
                </div>
            )}
            {groups.map((g) => (
                <section key={g.label} className="mb-1">
                    <div className="ps-label mb-1.5 mt-6 first:mt-0">
                        {g.label}
                    </div>
                    {g.rows.map(row)}
                </section>
            ))}
        </main>
    );
}
